import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Loader2, Eye, EyeOff, Mail, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import { tidyProfile } from '@/lib/customerFormat';

const COUNTRIES = [
  'United Kingdom', 'United States', 'Australia', 'Canada', 'Ireland',
  'New Zealand', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands',
  'Belgium', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Switzerland',
  'Austria', 'Portugal', 'Poland', 'Czech Republic', 'Hungary',
  'Romania', 'Bulgaria', 'Greece', 'Croatia', 'Slovakia', 'Slovenia',
  'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Cyprus',
  'Japan', 'South Korea', 'Singapore', 'United Arab Emirates', 'Other',
];

const Field = ({ label, children }) => (
  <div>
    <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">{label}</Label>
    {children}
  </div>
);

const inputCls = "bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";

export default function RegisterForm({ onBack, onSuccess, initialEmail = '' }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: initialEmail, phone: '',
    password: '', confirm_password: '',
    address_1: '', address_2: '', address_3: '',
    city: '', county: '', postcode: '', country: 'United Kingdom',
  });
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
      await base44.auth.register({
        email: form.email,
        password: form.password,
        full_name: `${capitalize(form.first_name)} ${capitalize(form.last_name)}`.trim(),
      });
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!code || code.length < 4) {
      setError('Please enter the verification code from your email.');
      return;
    }
    setVerifying(true);
    try {
      await base44.auth.verifyOtp({ email: form.email, otpCode: code });
      await base44.auth.loginViaEmailPassword(form.email, form.password);
      // Claim any pending admin invitation for this email (best-effort).
      let promoted = false;
      try { const r = await base44.functions.invoke('claimAdminInvite', {}); promoted = !!r.data?.promoted; } catch (e) { /* ignore */ }
      const tidy = tidyProfile({
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        address_line1: form.address_1,
        address_line2: form.address_2,
        city: form.city,
        postcode: form.postcode,
      });
      const fullName = `${tidy.first_name} ${tidy.last_name}`.trim();
      await base44.auth.updateMe({
        full_name: fullName,
        first_name: tidy.first_name,
        last_name: tidy.last_name,
        phone: tidy.phone,
        delivery_address_line1: tidy.address_line1,
        delivery_address_line2: tidy.address_line2,
        delivery_city: tidy.city,
        delivery_postcode: tidy.postcode,
      });
      // If an admin invite was claimed, re-login so the session token picks up the admin role.
      if (promoted) {
        try { await base44.auth.loginViaEmailPassword(form.email, form.password); } catch (e) { /* ignore */ }
      }
      onSuccess(fullName);
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    }
    setVerifying(false);
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="w-full max-w-md px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
            </div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2 text-center">VERIFY YOUR EMAIL</p>
            <h1 className="text-white font-black text-3xl uppercase text-center mb-3">CHECK YOUR INBOX</h1>
            <p className="text-white/30 text-sm text-center mb-8">
              We've sent a verification code to <span className="text-white">{form.email}</span>. Enter it below to activate your account.
            </p>
            <form onSubmit={handleVerify} className="space-y-5">
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">VERIFICATION CODE</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 text-center text-xl tracking-widest"
                  placeholder="Enter code"
                  maxLength={8}
                />
              </div>
              {error && (
                <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>
              )}
              <button
                type="submit"
                disabled={verifying}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {verifying ? 'VERIFYING...' : 'VERIFY & ACTIVATE'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16 pb-20">
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

          <p className="text-primary text-xs font-bold tracking-widest mb-3">CUSTOMER PORTAL</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase mb-2">CREATE YOUR ACCOUNT</h1>
          <p className="text-white/30 text-sm mb-10">Fill in your details below to register.</p>

          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Personal Details */}
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-5 pb-3 border-b border-white/5">PERSONAL DETAILS</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="FIRST NAME *">
                  <Input value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="Your first name" />
                </Field>
                <Field label="SURNAME *">
                  <Input value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="Your surname" />
                </Field>
                <Field label="EMAIL ADDRESS *">
                  <Input type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="your@email.com" />
                </Field>
                <Field label="TELEPHONE">
                  <Input type="tel" value={form.phone} onChange={set('phone')} className={inputCls} placeholder="Your phone number" />
                </Field>
              </div>
            </div>

            {/* Password */}
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-5 pb-3 border-b border-white/5">PASSWORD</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="YOUR PASSWORD *">
                  <div className="relative">
                    <Input
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={set('password')}
                      className={`${inputCls} pr-10`}
                      placeholder="Min. 6 characters"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>
                <Field label="CONFIRM PASSWORD *">
                  <div className="relative">
                    <Input
                      type={showPw2 ? 'text' : 'password'}
                      value={form.confirm_password}
                      onChange={set('confirm_password')}
                      className={`${inputCls} pr-10`}
                      placeholder="Repeat your password"
                    />
                    <button type="button" onClick={() => setShowPw2(!showPw2)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                      {showPw2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-5 pb-3 border-b border-white/5">YOUR ADDRESS</p>
              <div className="space-y-4">
                <Field label="1ST LINE ADDRESS">
                  <Input value={form.address_1} onChange={set('address_1')} className={inputCls} placeholder="House number & street name" />
                </Field>
                <Field label="2ND LINE ADDRESS">
                  <Input value={form.address_2} onChange={set('address_2')} className={inputCls} placeholder="Apartment, suite, etc. (optional)" />
                </Field>
                <Field label="3RD LINE ADDRESS">
                  <Input value={form.address_3} onChange={set('address_3')} className={inputCls} placeholder="(optional)" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="TOWN / CITY">
                    <Input value={form.city} onChange={set('city')} className={inputCls} placeholder="Town or city" />
                  </Field>
                  <Field label="STATE / COUNTY">
                    <Input value={form.county} onChange={set('county')} className={inputCls} placeholder="State or county" />
                  </Field>
                  <Field label="POSTCODE / ZIP CODE">
                    <Input value={form.postcode} onChange={set('postcode')} className={inputCls} placeholder="e.g. CH62 3RA" />
                  </Field>
                  <Field label="COUNTRY">
                    <select
                      value={form.country}
                      onChange={set('country')}
                      className="w-full h-11 bg-[#111115] border border-white/10 text-white text-sm px-3 focus:outline-none focus:border-primary/60 transition-colors"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c} className="bg-[#111115]">{c}</option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
            </div>

            {/* Marketing opt-in */}
            <div>
              <button
                type="button"
                onClick={() => setMarketing(!marketing)}
                className={`w-full flex items-start gap-4 p-4 border transition-all text-left ${
                  marketing
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-white/10 hover:border-white/20 bg-[#111115]'
                }`}
              >
                <div className={`mt-0.5 w-5 h-5 shrink-0 border-2 flex items-center justify-center transition-colors ${
                  marketing ? 'border-primary bg-primary' : 'border-white/20'
                }`}>
                  {marketing && <span className="text-white text-xs font-black">✓</span>}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-primary" />
                    <p className="text-white font-bold text-sm">JOIN OUR MAILING LIST</p>
                  </div>
                  <p className="text-white/40 text-xs leading-relaxed">
                    Receive exclusive offers, new product launches, and sim racing news from Motion Reality. You can unsubscribe at any time.
                  </p>
                </div>
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
              </button>
              <button
                type="button"
                onClick={onBack}
                className="sm:w-auto px-8 border border-white/10 hover:border-white/25 text-white/40 hover:text-white font-bold text-sm py-4 transition-all"
              >
                BACK
              </button>
            </div>

            <p className="text-white/20 text-xs text-center">
              Already have an account?{' '}
              <button type="button" onClick={onBack} className="text-primary hover:underline">Sign in here</button>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}